import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, BadgeCheck, ShieldCheck, Brain, Award, PenLine,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import Navbar from '../components/Navbar';
import SiteFooter from '../components/SiteFooter';
import ClosingCtaSection from '../components/ClosingCtaSection';
import TwoWaysSection from '../components/TwoWaysSection';
import TestimonialMarquee from '../components/TestimonialMarquee';
import FounderWelcomeVideo from '../components/FounderWelcomeVideo';

// ─── Shared: stats strip ──────────────────────────────────────────────────────

// Claims here must be ones we can stand behind today. A member count and a
// "KYC-verified members only" badge were removed: KYC is not built yet, and
// there is no verified-member figure to cite.
const STATS_BAR = [
  { icon: ShieldCheck, text: 'Private and secure by design' },
  { icon: Brain,       text: 'Psychology-led, not algorithm-led' },
  { icon: BadgeCheck,  text: 'Free to begin' },
];

const StatsBar = () => (
  <div className="w-full" style={{ background: '#0e1d33' }}>
    <div className="container mx-auto px-6 lg:px-24 py-[15px] flex flex-wrap items-center justify-center gap-x-10 gap-y-2">
      {STATS_BAR.map(({ icon: Icon, text }) => (
        <span key={text} className="flex items-center gap-2 text-base text-white">
          <Icon className="w-6 h-6" style={{ color: '#f1b200' }} strokeWidth={1.75} />
          {text}
        </span>
      ))}
    </div>
  </div>
);

// ─── Section 1: Hero ─────────────────────────────────────────────────────────

const HeroSection = ({ ctaHref }) => (
  <section className="relative overflow-hidden" style={{ background: '#08111f' }}>
    <Navbar transparent />
    <img
      src="/homepage-hero-couple.webp"
      alt="Couple who found compatibility through SoulSathiya"
      className="block w-full h-[420px] object-cover object-[62%_center] lg:object-center lg:absolute lg:inset-0 lg:h-full"
      loading="eager"
      fetchPriority="high"
    />
    <div
      className="absolute inset-0 hidden lg:block"
      style={{ backgroundImage: 'linear-gradient(180deg, rgba(0,0,0,0.32) 33.936%, rgba(0,0,0,0) 47.172%), linear-gradient(90deg, rgba(0,0,0,0.53) 43.45%, rgba(0,0,0,0) 61.908%)' }}
    />
    <div className="container mx-auto px-5 lg:px-[98px] relative z-10 pt-8 pb-10 lg:pt-32 lg:pb-44">
      <div className="max-w-[776px] space-y-4">
        <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-normal bg-white text-[#0b1729]">
          <span className="w-2 h-2 rounded-full bg-primary" />
          India's first relationship Intelligence platform
        </span>
        <div className="space-y-5">
          <h1 className="font-heading font-medium text-[40px] sm:text-[48px] leading-[1.05] sm:leading-[56px] tracking-[-0.96px]" style={{ color: '#fff9ea' }}>
            A <span style={{ color: '#dca200' }}>deeper way</span> to understand who
            <br className="hidden sm:block" /> you're choosing and why.
          </h1>
          <p className="text-lg leading-7 text-white max-w-[633px]">
            SoulSathiya uses relationship psychology to help you understand who you're
            choosing  and why they're genuinely compatible with you.
          </p>
        </div>
        <div className="space-y-3 pt-6">
          <div className="flex flex-col sm:flex-row gap-3">
            <Link to={ctaHref}>
              <Button className="h-auto px-5 py-4 text-base font-medium w-full sm:w-auto rounded-xl hover:opacity-90" style={{ background: '#f1b200', color: '#0e1d33' }}>
                Find my match — it's free <ArrowRight className="w-5 h-5 ml-1" />
              </Button>
            </Link>
            <Link to="/couple">
              <Button className="h-auto px-5 py-4 text-base font-medium w-full sm:w-auto rounded-xl hover:opacity-90" style={{ background: 'white', color: '#0e1d33' }}>
                Check Couple Compatibility
              </Button>
            </Link>
          </div>
          <p className="text-base leading-5 text-white opacity-[0.83]">Matchmaking is free to join</p>
        </div>
      </div>
    </div>
    {/* Mobile (Figma 1086:264): plain stats band below the hero content */}
    <div className="lg:hidden" style={{ background: '#0e1d33' }}>
      <div className="px-5 py-4 flex flex-col items-center gap-2">
        {STATS_BAR.map(({ icon: Icon, text }) => (
          <span key={text} className="flex items-center gap-2 text-base text-white">
            <Icon className="w-6 h-6" style={{ color: '#f1b200' }} strokeWidth={1.75} />
            {text}
          </span>
        ))}
      </div>
    </div>
    {/* Curved dark base with the stats strip, matching Figma's masked hero edge */}
    <div className="hidden lg:block absolute bottom-0 left-0 right-0 z-10">
      <svg className="w-full block" viewBox="0 0 1440 60" preserveAspectRatio="none" style={{ height: 60 }} aria-hidden="true">
        <path d="M0,60 L0,28 Q720,-24 1440,28 L1440,60 Z" fill="#08111f" />
      </svg>
      <div style={{ background: '#08111f' }}>
        <div className="container mx-auto px-6 lg:px-24 pb-6 pt-1 flex flex-wrap items-center justify-center gap-x-10 gap-y-2">
          {STATS_BAR.map(({ icon: Icon, text }) => (
            <span key={text} className="flex items-center gap-2 text-base text-white">
              <Icon className="w-6 h-6" style={{ color: '#f1b200' }} strokeWidth={1.75} />
              {text}
            </span>
          ))}
        </div>
      </div>
    </div>
  </section>
);

// ─── Section 2: What we believe ──────────────────────────────────────────────

const BuiltToUnderstandSection = () => (
  <section className="pt-[68px] pb-14 px-6 lg:px-24">
    <div className="container mx-auto space-y-6">
      <div className="flex items-center gap-2 text-base" style={{ color: '#f1b200' }}>
        <span className="w-2 h-2 rounded-full" style={{ background: '#f1b200' }} />
        What we believe
      </div>
      <div className="flex flex-col lg:flex-row gap-8 lg:gap-[132px] items-start">
        <h2 className="font-heading font-medium text-[32px] sm:text-[40px] leading-[1.2] sm:leading-[48px] tracking-[-0.4px] lg:w-[450px] lg:shrink-0" style={{ color: '#fff9ea' }}>
          Built to <span style={{ color: '#f1b200' }}>understand compatibility</span>, not just find options.
        </h2>
        <div className="flex-1 space-y-6 text-base leading-6 text-white">
          <p>
            Matrimony sites filter by height, salary, and horoscope. Dating apps
            filter by photos. Both help you find options, not understand them.
          </p>
          <p>
            SoulSathiya was built differently. Choosing a life partner deserves more
            than a list of attributes. It deserves understanding of your personality,
            your values, and whether the person in front of you is genuinely aligned
            with the life you're building. We call this relationship intelligence.
          </p>
        </div>
      </div>
    </div>
  </section>
);

// ─── Section 3: Founder ───────────────────────────────────────────────────────

const FOUNDER_CREDENTIALS = [
  { icon: Award,   label: '30+ Years of Military Leadership' },
  { icon: Brain,   label: 'Certified Psychology Counsellor' },
  { icon: PenLine, label: 'Author of 21 Secrets to Eternal Love' },
];

const FounderQuoteSection = () => (
  <section className="py-14 px-6 lg:px-24" style={{ background: '#fff9ea' }}>
    <div className="container mx-auto">
      <div
        className="rounded-[32px] p-8 flex flex-col md:flex-row gap-10 items-center justify-center"
        style={{ background: '#fff0c7', border: '1px solid #ffe7a3' }}
      >
        <div className="rounded-3xl overflow-hidden flex-shrink-0 mx-auto md:mx-0" style={{ width: 378, maxWidth: '100%', aspectRatio: '377.647 / 488.534' }}>
          {/* The homepage quote uses its own portrait (the About page hero has a
              different one). Its 0.773 aspect already matches this card, so
              object-cover has nothing to crop. It is also the cover of his
              one-minute welcome video, which plays only when pressed. */}
          <FounderWelcomeVideo posterSrc="/founder-home.webp" alt="Col Rakesh Dogra (Retd) — Founder, SoulSathiya" />
        </div>
        <div className="flex-1 space-y-6">
          <div className="flex items-center gap-2 text-base" style={{ color: '#204377' }}>
            <span className="w-2 h-2 rounded-full" style={{ background: '#204377' }} />
            A word from our founder
          </div>
          <div className="space-y-1">
            <p className="font-heading font-bold text-[40px] sm:text-[48px] leading-[56px] tracking-[-0.96px]" style={{ color: '#152c4f' }}>Col Rakesh Dogra (Retd)</p>
            <p className="text-lg leading-7" style={{ color: '#393939' }}>Founder, SoulSathiya</p>
          </div>
          <div className="flex flex-wrap gap-4">
            {FOUNDER_CREDENTIALS.map(({ icon: Icon, label }) => (
              <span key={label} className="inline-flex items-center gap-2 text-sm px-3 py-2 rounded-[22px]" style={{ background: '#fff9ea', border: '1px solid #f1b200', color: '#152c4f' }}>
                <Icon className="w-6 h-6 shrink-0" style={{ color: '#152c4f' }} strokeWidth={1.75} />
                {label}
              </span>
            ))}
          </div>
          <p className="font-medium text-base leading-6 px-8" style={{ borderLeft: '4px solid #dca200', color: '#aa7d00' }}>
            “We spend so much time preparing for a career, choosing a home and planning our future. Yet when it comes to relationships, we often rely on hope. I built SoulSathiya to bring understanding and compatibility into every stage of the relationship journey , from choosing a life partner to building a lasting, fulfilling relationship.”
          </p>
          <div className="flex justify-end px-5 py-2">
            <Link to="/about" className="inline-flex items-center gap-1 font-medium text-base underline" style={{ color: '#204377' }}>
              Read Col Dogra's full story <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// ─── Section 4: How we measure ───────────────────────────────────────────────

// Focal points for the full-resolution originals (faces sit in the upper-middle of most)
const DIM_FOCUS = {
  '/couples-dim-emotional-depth.webp': 'center 45%',
  '/couples-dim-communication.webp': 'center 18%',
  '/couples-dim-conflict-resolution.webp': 'center 35%',
  '/couples-dim-values-vision.webp': 'center 22%',
  '/couples-dim-trust-commitment.webp': 'center 30%',
  '/couples-dim-lifestyle-harmony.webp': 'center 45%',
  '/couples-dim-growth-together.webp': 'center 30%',
  '/couples-dim-intimacy-affection.webp': 'center 35%',
};

const DIMENSION_CARDS = [
  { title: 'Emotional Depth',      text: 'How you each give and receive love and whether your emotional needs are being met in the ways that matter to you.', img: '/couples-dim-emotional-depth.webp' },
  { title: 'Communication',        text: 'How openly you each share, how well you listen, and whether the way you talk to each other supports or strains the relationship', img: '/couples-dim-communication.webp' },
  { title: 'Conflict Resolution',  text: 'How you each handle disagreement, whether you move toward resolution or away from it, and what your patterns look like under pressure.', img: '/couples-dim-conflict-resolution.webp' },
  { title: 'Values & Vision',      text: "What you're each building toward, your beliefs, priorities, and the kind of life you're working to create together", img: '/couples-dim-values-vision.webp' },
  { title: 'Trust & Commitment',   text: 'How secure you each feel in the relationship and how aligned you are in your understanding of what commitment means.', img: '/couples-dim-trust-commitment.webp' },
  { title: 'Lifestyle Harmony',    text: 'Your daily rhythms, habits, and expectations around how life is actually lived, not just the big decisions, but the small ones that accumulate.', img: '/couples-dim-lifestyle-harmony.webp' },
  { title: 'Growth Together',      text: "Whether you're growing in the same direction and whether your individual ambitions and the relationship's future are in conversation with each other.", img: '/couples-dim-growth-together.webp' },
  { title: 'Intimacy & Affection', text: 'How both partners create and respond to closeness through initiating warmth, listening without defensiveness, and expressing love.', img: '/couples-dim-intimacy-affection.webp' },
];

const HowWeMeasureSection = () => (
  <section
    className="py-24 px-6 lg:px-24"
    style={{ backgroundImage: "url('/method-hero-bg-pattern.webp')", backgroundSize: '100% auto', backgroundRepeat: 'repeat-y', backgroundColor: '#08111f' }}
  >
    <div className="container mx-auto space-y-6">
      <div className="flex items-center gap-2 text-base" style={{ color: '#f1b200' }}>
        <span className="w-2 h-2 rounded-full" style={{ background: '#f1b200' }} />
        How it works
      </div>
      <div className="space-y-8">
        <h2 className="font-heading font-medium text-3xl sm:text-[48px] leading-[1.15] sm:leading-[56px] tracking-[-0.96px]" style={{ color: '#fff9ea' }}>
          How we measure <span style={{ color: '#f1b200' }}>Relationship Compatibility</span>
        </h2>

        <div className="space-y-10">
          <div className="flex flex-col lg:flex-row gap-5 items-center">
            <div className="flex-1 space-y-2 px-4 py-11">
              <p className="text-lg leading-7 text-white">
                Soulsathiya combines multiple psychological frameworks into a single assessment.
              </p>
              <p className="font-heading font-medium text-[28px] sm:text-4xl leading-10 max-w-[450px]" style={{ color: '#f1b200' }}>
                One assessment. 36 questions. 8 dimensions of relationship compatibility.
              </p>
            </div>
            <img
              src="/ri-radar-chart.webp"
              alt="Radar chart showing compatibility across eight dimensions"
              className="w-full max-w-[711px] h-auto flex-shrink-0"
              loading="lazy"
            />
          </div>

          <div className="flex overflow-x-auto snap-x snap-mandatory gap-4 -mx-6 px-6 pb-2 sm:mx-0 sm:px-0 sm:pb-0 sm:grid sm:grid-cols-2 sm:gap-5 sm:overflow-visible" style={{ scrollbarWidth: 'none' }}>
            {DIMENSION_CARDS.map(({ title, text, img }) => (
              <div key={title} className="snap-start shrink-0 w-[85%] sm:w-auto sm:shrink rounded-xl p-6 sm:p-8 flex flex-col gap-4" style={{ background: '#0b1729' }}>
                <img src={img} alt={title} className="w-full h-[226px] object-cover rounded-xl" style={{ objectPosition: DIM_FOCUS[img] || 'center 35%' }} loading="lazy" />
                <div className="space-y-[11px]">
                  <h3 className="font-body font-medium text-xl leading-7 text-white">{title}</h3>
                  <p className="text-base leading-6 text-white">{text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  </section>
);

// ─── Section 5: Go beyond profiles ───────────────────────────────────────────

const GoBeyondSection = () => (
  <section className="py-14 px-6 lg:px-24">
    <div className="container mx-auto flex flex-col items-center gap-7 text-center">
      <h2 className="font-heading font-medium text-3xl sm:text-[48px] leading-[1.15] sm:leading-[56px] tracking-[-0.96px]">
        <span className="block" style={{ color: '#fff9ea' }}>Go beyond profiles. Understand each other.</span>
        <span className="block" style={{ color: '#f1b200' }}>A psychological portrait of you and your potential match.</span>
      </h2>
      <Link to="/methodology">
        <Button className="h-14 px-5 rounded-xl font-medium hover:opacity-90" style={{ background: '#f1b200', color: '#0e1d33' }}>
          Discover how we measure compatibility <ArrowRight className="w-5 h-5 ml-1" />
        </Button>
      </Link>
    </div>
  </section>
);

// ─── Section 7: Testimonials ──────────────────────────────────────────────────

// Placeholder testimonials pending real, consented customer quotes.
// Three entries, three distinct photos — never reuse a face under two names,
// and never caption a photo with a mismatched gender (both happened before).
// Swap the copy and photos for genuine ones before any paid-acquisition push.
const TESTIMONIALS = [
  { photo: '/tst-ananya.webp', title: 'A match that felt right', text: 'I was looking for someone who shared my values, not just someone with a similar profile. The conversation felt effortless from the very beginning.', name: 'Ananya', city: 'Delhi' },
  { photo: '/tst-pranav.webp', title: 'Beyond just a profile', text: 'What stood out was how thoughtfully the matches were curated. We connected over our families, ambitions, and the kind of life we wanted to build.', name: 'Pranav', city: 'Pune' },
  { photo: '/tst-akila.webp',  title: 'A conversation worth continuing', text: 'I had almost stopped believing I could find a meaningful connection this way. Then I met someone who genuinely understood what I was looking for.', name: 'Akila R.', city: 'Bangalore' },
];

const TestimonialCard = ({ photo, title, text, name, city }) => (
  <div className="flex-1 min-w-0 flex flex-col">
    <div className="relative" style={{ height: 122 }}>
      <div className="absolute inset-x-0 bottom-0 h-[61px]" style={{ background: '#f1b200' }} />
      <div className="absolute left-1/2 -translate-x-1/2 top-0 rounded-full overflow-hidden" style={{ width: 122, height: 122, background: '#0b1729' }}>
        <img src={photo} alt="" aria-hidden="true" className="w-full h-full object-cover" loading="lazy" />
      </div>
    </div>
    <div className="flex flex-col gap-4 p-8 flex-1" style={{ background: '#0b1729' }}>
      <p className="text-xl font-medium leading-7 text-white">{title}</p>
      <p className="text-base leading-6 text-white">{text}</p>
      <div className="mt-auto">
        <p className="text-xl font-medium leading-7" style={{ color: '#c6c6c6' }}>{name}</p>
        <p className="text-base leading-6" style={{ color: '#aaaaaa' }}>{city}</p>
      </div>
    </div>
  </div>
);

const TestimonialsSection = () => (
  <section id="testimonials" className="pt-14 scroll-mt-24">
    <div className="container mx-auto space-y-8 px-6 lg:px-24">
      <div className="space-y-6">
        <div className="flex items-center gap-2 text-base" style={{ color: '#f1b200' }}>
          <span className="w-2 h-2 rounded-full" style={{ background: '#f1b200' }} />
          Real people. Real decisions
        </div>
        <h2 className="font-heading font-medium text-3xl sm:text-[48px] leading-[1.15] sm:leading-[56px] tracking-[-0.96px] text-white">
          What <span style={{ color: '#f1b200' }}>clarity</span> feels like -  in their own words.
        </h2>
      </div>
    </div>
    {/* Continuously scrolling rail — pauses on hover so a card can be read. */}
    <TestimonialMarquee
      items={TESTIMONIALS}
      itemKey={(t) => t.name + t.title}
      className="mt-8"
      renderItem={(t) => <TestimonialCard {...t} />}
    />
  </section>
);

// ─── Section 8: Closing CTA ────────────────────────────────────────────────────


// ─── Sticky Mobile CTA ─────────────────────────────────────────────────────────

const StickyMobileCta = ({ ctaHref, show }) => (
  <div
    className={`fixed bottom-0 left-0 right-0 z-50 md:hidden transition-all duration-300 ${show ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'}`}
    style={{ background: 'linear-gradient(to top, rgba(12,19,35,0.98) 60%, rgba(12,19,35,0.85))', borderTop: '1px solid rgba(212,165,32,0.25)', backdropFilter: 'blur(12px)' }}
  >
    <div className="px-4 py-3 flex items-center gap-3">
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-foreground truncate">Find your compatible match</p>
        <p className="text-xs text-muted-foreground">Free to start · Psychology-driven</p>
      </div>
      <Link to={ctaHref} className="flex-shrink-0">
        <button className="px-5 py-2.5 rounded-xl text-sm font-medium whitespace-nowrap" style={{ background: '#f1b200', color: '#0e1d33' }}>
          Get Started
        </button>
      </Link>
    </div>
  </div>
);

// ─── Page ─────────────────────────────────────────────────────────────────────

const LandingPage = () => {
  const isLoggedIn = !!localStorage.getItem('session_token');
  const ctaHref = isLoggedIn ? '/dashboard' : '/register';
  // `?ref=CODE` is caught on every route by components/ReferralCapture.jsx.

  const [showStickyCta, setShowStickyCta] = useState(false);
  useEffect(() => {
    const handleScroll = () => setShowStickyCta(window.scrollY > 500);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <HeroSection ctaHref={ctaHref} />
      <BuiltToUnderstandSection />
      <FounderQuoteSection />
      <HowWeMeasureSection />
      <GoBeyondSection />
      <TwoWaysSection />
      <TestimonialsSection />
      <div className="mt-[70px]"><StatsBar /></div>
      <ClosingCtaSection ctaHref={ctaHref} />
      <SiteFooter />
      <StickyMobileCta ctaHref={ctaHref} show={showStickyCta} />
    </div>
  );
};

export default LandingPage;
